import React, { Component, Suspense } from 'react';
import packageJson from '../package.json';
import { connect } from 'react-redux';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { initApex, setGateway } from '@alphapoint/apex-web/lib/apex';
import { connectionOpened, connectionClosed } from '@alphapoint/apex-web/lib/redux/actions/wsConnectionActions';
import { getQueryParam } from './helpers/queryParamsHelper';
import { onInit } from '@alphapoint/apex-web/lib/redux/actions/lifeCycleActions';
import StandAloneLayout from './layouts/StandAloneLayout/StandAloneLayout';
import './styles/theme/dark.css';
import SignupPage from './pages/SignupPage';
import ProblemLoggingInPage from './pages/ProblemLoggingInPage';
import DepositConfirmation from '@alphapoint/apex-web/lib/components/DepositConfirmation/DepositConfirmationModalContainer';
import Snackbar from '@alphapoint/apex-web/lib/components/APSnackbar/APSnackbarContainer';
import TwoFactor from '@alphapoint/apex-web/lib/components/TwoFactorAuthForm/TwoFactorAuthFormContainer';
import SidePane from '@alphapoint/apex-web/lib/components/common/SidePane/SidePaneContainer';
import { emailLinkRoutes } from '@alphapoint/apex-web/lib/helpers/emailLinksHelper';
import EmailLinks from '@alphapoint/apex-web/lib/layouts/EmailLinksLayout.js';
import InteriorPage from './pages/InteriorPage';
import LoginFormContainer from '@alphapoint/apex-web/lib/components/Login/LoginFormContainer';
import { getGateway } from '@alphapoint/apex-web/lib/helpers/wsHelper';
import redirectIfAuthorized from '@alphapoint/apex-web/lib/hocs/redirectIfAuthorized';
import { SimpleExchangePage } from './pages/Exchange/SimpleExchangePage';
import config from './config';
import Require2FA from '@alphapoint/apex-web/lib/components/2FA/TwoFactorAuthContainer';
import Enable2FA from '@alphapoint/apex-web/lib/components/EnableTwoFactorAuth/StandaloneEnable2FAModalContainer';
import LandingPage from './pages/LandingPage/LandingPage';
import SendReceiveTwoFactorAuthContainer from '@alphapoint/apex-web/lib/components/SendReceiveSidePane/SendReceiveTwoFactorAuth/SendReceiveTwoFactorAuthContainer';
import StandAloneKyc from '@alphapoint/apex-web/lib/layouts/StandAloneKyc';
// KYC Provider: pass your KYC form component as the FormComponent prop to StandAloneKyc.
// To use a different provider, replace this import with your own component:
//   import MyCustomKYCForm from './components/MyCustomKYC/MyCustomKYCForm';
// Then pass it below: <StandAloneKyc FormComponent={MyCustomKYCForm} />
import KYCSumSubSdkContainer from './components/KYCSumSub/KYCSumSubSdkContainer';
// DEV-8959: Travel Rule deposit modal
import TravelRuleDepositModal from '@alphapoint/apex-web/lib/components/TravelRuleDepositModal';
import {
  marginBorrowerEnabledSelector,
  marginSystemEnabledSelector,
} from '@alphapoint/apex-web/lib/redux/margin/selectors/marginSelectors';
import { ComponentsPage } from './demo/ComponentsPage';

class App extends Component {
  constructor(props) {
    super(props);

    const gateway = getGateway();
    if (gateway) {
      setGateway(gateway);
      initApex(this.props.connectionOpened, this.props.connectionClosed);
    }

    const AffiliateTag = getQueryParam('aff');
    if (AffiliateTag) {
      window.localStorage.setItem('affiliateTag', AffiliateTag);
    }
  }

  render() {
    const { isAuthenticated } = this.props;
    const { pathname } = this.props.location;
    const buildSimpleExchangePath = (symbol) => {
      const { SimpleExchange } = config;

      if (!SimpleExchange?.active || !SimpleExchange?.route) {
        return '/';
      }

      if (!symbol) {
        return SimpleExchange.route;
      }

      return SimpleExchange.route.endsWith('/')
        ? `${SimpleExchange.route}${symbol}`
        : `${SimpleExchange.route}/${symbol}`;
    };

    return (
      <Suspense fallback={null}>
        <div className="App fluid container">
          <Switch>
            <Route
              path="/version"
              render={() => (
                <div>
                  <p>v3implementation-template: {packageJson.version}</p>
                  <p>apex-web: {packageJson.dependencies['@alphapoint/apex-web']}</p>
                </div>
              )}
            />
            <Route exact path={'/'} component={LandingPage} />
            {emailLinkRoutes(pathname, EmailLinks)}
            <Route path={'/login'} component={redirectIfAuthorized(LoginFormContainer)} />
            <Route path={'/standalonekyc'}
              render={() => <StandAloneKyc FormComponent={KYCSumSubSdkContainer} />} />
            <Route path={'/signup'} component={SignupPage} />
            <Route path={'/problem-logging-in'} component={ProblemLoggingInPage} />
            <Route
              path={'/twofactorauth'}
              render={() => (
                <StandAloneLayout>
                  <TwoFactor />
                </StandAloneLayout>
              )}
            />
            {!isAuthenticated && !localStorage.getItem('token') && (
              <Route
                path={'/exchange/:rest(.*)?'}
                render={({ match }) => {
                  const rest = match?.params?.rest || '';
                  const segments = rest.split('/').filter(Boolean);
                  const symbol = [...segments]
                    .reverse()
                    .find((segment) => !['spot', 'margin', 'futures'].includes(segment.toLowerCase()));

                  return <Redirect to={buildSimpleExchangePath(symbol)} />;
                }}
              />
            )}
            {!isAuthenticated && !localStorage.getItem('token') && config.SimpleExchange.active && (
              <Route path={`${config.SimpleExchange.route}/:symbol?`} component={SimpleExchangePage} />
            )}
            <Route path={'/enabletwofactorauth'} render={() => <Enable2FA />} />
            <Route path="/components" component={ComponentsPage} />
            {/* passing props.location prevents Blocked Re-renders: https://reacttraining.com/react-router/web/guides/dealing-with-update-blocking */}
            <InteriorPage
              location={this.props.location}
              systemMarginEnabled={this.props.systemMarginEnabled}
              marginBorrowerEnabled={this.props.marginBorrowerEnabled}
            />
          </Switch>
        </div>
        <Snackbar />
        <SidePane
          options={{
            ReportBlockTrade: {
              useNewForm: true,
            },
          }}
        />
        <DepositConfirmation />
        <Require2FA />
        <SendReceiveTwoFactorAuthContainer />
        <TravelRuleDepositModal />
      </Suspense>
    );
  }
}

const mapStateToProps = ({ auth: { isAuthenticated }, ...state }) => ({
  isAuthenticated,
  systemMarginEnabled: marginSystemEnabledSelector(state),
  marginBorrowerEnabled: marginBorrowerEnabledSelector(state),
});

const mapDispatchToProps = {
  onInit,
  connectionOpened,
  connectionClosed,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
